import { useCallback, useEffect, useState } from 'react';
import { Header, Button, Badge, Card, Input, Select, Textarea, Modal, EmptyState, Skeleton, useToast, LongText } from '../../shared/components';
import { useCurrentUser } from '../../shared/auth/AuthProvider';
import { getTurnoGeoRepository } from '../../shared/services/turnoGeo/repositoryFactory';
import { getSettingsRepository } from '../../shared/services/settings/repositoryFactory';
import { ApplicationMember } from '../../shared/services/settings/types';
import { NOVELTY_TYPE_LABEL, Novelty, NoveltyStatus, NoveltyType } from '../../shared/services/turnoGeo/types';
import { InfoTip } from './components/InfoTip';
import { shortDateTime } from './jornadaUtil';

const repo = getTurnoGeoRepository();
const settingsRepo = getSettingsRepository();

const STATUS_TONE: Record<NoveltyStatus, 'warning' | 'success' | 'critical'> = { PENDIENTE: 'warning', APROBADA: 'success', RECHAZADA: 'critical' };
const STATUS_LABEL: Record<NoveltyStatus, string> = { PENDIENTE: 'Pendiente de aprobación', APROBADA: 'Aprobada', RECHAZADA: 'Rechazada' };
const TYPE_HELP: Record<NoveltyType, string> = {
  INCAPACIDAD: 'Incapacidad médica. La nota es opcional.',
  VACACIONES: 'Días de vacaciones. La nota es opcional.',
  PERMISO: 'Permiso para ausentarte o salir. Cuenta el motivo.',
  OLVIDO_CIERRE: 'Olvidaste marcar tu entrada, almuerzo o salida. Cuenta qué pasó y a qué hora fue.',
  OTRA: 'Cualquier otra situación que afecte tu jornada. Cuenta el motivo.',
};

const today = () => new Date().toLocaleDateString('en-CA');
const fmtDate = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
const range = (n: Novelty) => (n.startDate === n.endDate ? fmtDate(n.startDate) : `${fmtDate(n.startDate)} – ${fmtDate(n.endDate)}`);

/**
 * Novedades: el empleado avisa lo que afecta su jornada (incapacidad, vacaciones, permiso,
 * olvido de cierre u otra) y un líder (supervisor o administrador) la aprueba o rechaza. Una novedad
 * aprobada ayuda a justificar las inconsistencias de esos días.
 */
export function NovedadesPage() {
  const toast = useToast();
  const user = useCurrentUser();
  const isLeader = user.role === 'admin' || user.role === 'supervisor';
  const [tab, setTab] = useState<'mias' | 'aprobar'>('mias');
  const [mine, setMine] = useState<Novelty[] | null>(null);
  const [pending, setPending] = useState<Novelty[] | null>(null);
  const [members, setMembers] = useState<ApplicationMember[]>([]);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ userId: '', noveltyType: 'PERMISO' as NoveltyType, startDate: today(), endDate: today(), note: '' });
  const [deciding, setDeciding] = useState<{ item: Novelty; status: 'APROBADA' | 'RECHAZADA' } | null>(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [cancelling, setCancelling] = useState<Novelty | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(() => {
    repo.listNovelties({ userId: user.id }).then(setMine).catch(() => setMine([]));
    if (isLeader) repo.listNovelties({ status: 'PENDIENTE' }).then((l) => setPending(l.filter((n) => n.userId !== user.id))).catch(() => setPending([]));
  }, [user.id, isLeader]);

  useEffect(() => {
    load();
    if (isLeader) settingsRepo.listApplicationMembers('turno-geo', true).then((m) => setMembers(m.filter((x) => x.hasAccess)));
  }, [load, user.role]);

  const save = async () => {
    setIsSaving(true);
    try {
      await repo.createNovelty({ userId: form.userId || undefined, noveltyType: form.noveltyType, startDate: form.startDate, endDate: form.endDate, note: form.note });
      toast.show('Novedad enviada. Quedó pendiente de aprobación.');
      setCreating(false);
      setForm({ userId: '', noveltyType: 'PERMISO', startDate: today(), endDate: today(), note: '' });
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo registrar la novedad.', 'error');
    } finally {
      setIsSaving(false);
    }
  };
  const decide = async () => {
    if (!deciding) return;
    setIsSaving(true);
    try {
      await repo.reviewNovelty(deciding.item.id, { status: deciding.status, note: decisionNote });
      toast.show(deciding.status === 'APROBADA' ? 'Novedad aprobada.' : 'Novedad rechazada.');
      setDeciding(null);
      setDecisionNote('');
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar la decisión.', 'error');
    } finally {
      setIsSaving(false);
    }
  };
  const cancel = async () => {
    if (!cancelling) return;
    setIsSaving(true);
    try {
      await repo.deleteNovelty(cancelling.id);
      toast.show('Novedad cancelada.');
      setCancelling(null);
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo cancelar.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const card = (n: Novelty, actions: React.ReactNode, showUser: boolean) => (
    <Card key={n.id}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink-800">
            {NOVELTY_TYPE_LABEL[n.noveltyType]} <Badge tone={STATUS_TONE[n.status]}>{STATUS_LABEL[n.status]}</Badge>
          </p>
          {showUser && <p className="text-xs text-ink-500">{n.userFullName}{n.userDocumentNumber ? ` · CC ${n.userDocumentNumber}` : ''}</p>}
          <p className="mt-0.5 text-sm text-ink-700">{range(n)}</p>
          {n.note && <p className="mt-1 text-xs text-ink-600"><LongText text={n.note} max={100} /></p>}
          {n.reviewedBy && (
            <p className="mt-1 text-[11px] text-ink-400">
              {n.status === 'APROBADA' ? 'Aprobada' : 'Rechazada'} por {n.reviewedBy} · {n.reviewedDate ? shortDateTime(n.reviewedDate) : ''}
              {n.reviewNote ? ` · “${n.reviewNote}”` : ''}
            </p>
          )}
          <p className="text-[11px] text-ink-300">Enviada {shortDateTime(n.createdDate)}</p>
        </div>
        <div className="flex gap-2">{actions}</div>
      </div>
    </Card>
  );

  const list = (items: Novelty[] | null, empty: string, render: (n: Novelty) => React.ReactNode) =>
    items === null ? <Skeleton className="h-24 w-full" /> : items.length === 0 ? <EmptyState icon="🗂️" title="Sin novedades" description={empty} /> : <div className="space-y-3">{items.map(render)}</div>;

  return (
    <div>
      <Header title="Novedades" subtitle="TurnoGeo" />
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2">
            <Button size="md" variant={tab === 'mias' ? 'primary' : 'outline'} onClick={() => setTab('mias')}>Mis novedades</Button>
            {isLeader && (
              <Button size="md" variant={tab === 'aprobar' ? 'primary' : 'outline'} onClick={() => setTab('aprobar')}>
                Por aprobar{pending && pending.length > 0 ? ` (${pending.length})` : ''}
              </Button>
            )}
          </div>
          <Button size="md" onClick={() => setCreating(true)}>Nueva novedad</Button>
        </div>
        <p className="flex items-center gap-2 text-xs text-ink-500">
          Avisa aquí lo que afecte tu jornada.
          <InfoTip title="¿Para qué sirven?" text="Una incapacidad, unas vacaciones, un permiso o un olvido de marcación se registran como novedad. Un líder (supervisor o administrador) la aprueba o la rechaza. Las aprobadas ayudan a justificar las inconsistencias de esos días. Una novedad no cambia tus marcaciones: si olvidaste cerrar el turno, ciérralo desde Marcar." />
        </p>

        {tab === 'mias' && list(mine, 'Cuando tengas una incapacidad, vacaciones, permiso u olvido, regístralo con “Nueva novedad”.', (n) =>
          card(n, n.status === 'PENDIENTE' ? <Button size="md" variant="outline" onClick={() => setCancelling(n)}>Cancelar</Button> : null, false)
        )}
        {tab === 'aprobar' && isLeader && list(pending, 'No hay novedades esperando aprobación.', (n) =>
          card(
            n,
            <>
              <Button size="md" onClick={() => { setDeciding({ item: n, status: 'APROBADA' }); setDecisionNote(''); }}>Aprobar</Button>
              <Button size="md" variant="outline" onClick={() => { setDeciding({ item: n, status: 'RECHAZADA' }); setDecisionNote(''); }}>Rechazar</Button>
            </>,
            true
          )
        )}
      </div>

      <Modal open={creating} onClose={() => setCreating(false)} title="Nueva novedad">
        <div className="space-y-3">
          {isLeader && (
            <Select label="Para quién" value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} placeholder="Para mí" options={members.filter((m) => m.id !== user.id).map((m) => ({ value: m.id, label: m.fullName }))} />
          )}
          <Select label="Tipo" value={form.noveltyType} onChange={(e) => setForm({ ...form, noveltyType: e.target.value as NoveltyType })} options={(Object.keys(NOVELTY_TYPE_LABEL) as NoveltyType[]).map((t) => ({ value: t, label: NOVELTY_TYPE_LABEL[t] }))} />
          <p className="-mt-1 text-xs text-ink-500">{TYPE_HELP[form.noveltyType]}</p>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Desde" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value, endDate: e.target.value > form.endDate ? e.target.value : form.endDate })} />
            <Input label="Hasta" type="date" value={form.endDate} min={form.startDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
          </div>
          <Textarea label="Nota" value={form.note} maxLength={500} onChange={(e) => setForm({ ...form, note: e.target.value })} hint={['PERMISO', 'OLVIDO_CIERRE', 'OTRA'].includes(form.noveltyType) ? 'Obligatoria (mínimo 5 caracteres).' : 'Opcional.'} />
          <div className="flex gap-3 pt-1">
            <Button variant="outline" fullWidth onClick={() => setCreating(false)}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving} disabled={!form.startDate || !form.endDate || (['PERMISO', 'OLVIDO_CIERRE', 'OTRA'].includes(form.noveltyType) && form.note.trim().length < 5)}>Enviar</Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!deciding} onClose={() => setDeciding(null)} title={deciding?.status === 'APROBADA' ? 'Aprobar novedad' : 'Rechazar novedad'}>
        {deciding && (
          <div className="space-y-3">
            <div className="rounded-xl bg-ink-50 p-3 text-xs text-ink-600">
              <p className="font-semibold text-ink-800">{deciding.item.userFullName} · {NOVELTY_TYPE_LABEL[deciding.item.noveltyType]}</p>
              <p>{range(deciding.item)}</p>
              {deciding.item.note && <p className="mt-1">{deciding.item.note}</p>}
            </div>
            <Textarea label="Nota" value={decisionNote} maxLength={300} onChange={(e) => setDecisionNote(e.target.value)} hint={deciding.status === 'RECHAZADA' ? 'Obligatoria: explica por qué la rechazas (mínimo 5 caracteres).' : 'Opcional.'} />
            <div className="flex gap-3 pt-1">
              <Button variant="outline" fullWidth onClick={() => setDeciding(null)}>Volver</Button>
              <Button fullWidth variant={deciding.status === 'RECHAZADA' ? 'danger' : 'primary'} onClick={decide} isLoading={isSaving} disabled={deciding.status === 'RECHAZADA' && decisionNote.trim().length < 5}>
                {deciding.status === 'APROBADA' ? 'Aprobar' : 'Rechazar'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!cancelling} onClose={() => setCancelling(null)} title="Cancelar novedad">
        {cancelling && (
          <div className="space-y-3">
            <p className="text-sm text-ink-700">¿Cancelar tu novedad de {NOVELTY_TYPE_LABEL[cancelling.noveltyType].toLowerCase()} ({range(cancelling)})?</p>
            <div className="flex gap-3">
              <Button variant="outline" fullWidth onClick={() => setCancelling(null)}>No</Button>
              <Button variant="danger" fullWidth onClick={cancel} isLoading={isSaving}>Sí, cancelar</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
